import { isAxiosError } from "axios";
import {
  Link,
  type LoaderFunction,
  Navigate,
  NavLink,
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  useRouteError,
} from "react-router-dom";

import { locationBrandingStyle } from "@/modules/platformLocation/locationBrandingStyle";
import { useLocationLogo } from "@/modules/platformLocation/useLocationLogo";
import { WalletAvailabilityWarmup } from "@/modules/stripe/WalletAvailabilityWarmup";
import { useTranslation } from "@/core/i18n";
import { cn } from "@/lib/utils";
import { usePlatformDomainTokens } from "hooks/usePlatformDomainTokens";
import {
  isAnonymousBrowsablePath,
  setPostLoginReturnTo,
  useIsAnonymousBrowsing,
} from "modules/anonymousBrowsing";
import { standaloneProductsPaths } from "modules/standaloneProducts/standaloneProductsPaths";
import { TopNavActions } from "molecules/navbars/TopNavActions";
import { AccessTemporarilySuspendedModal } from "organisms/access-temporarily-suspended-modal/AccessTemporarilySuspendedModal";
import { DisputeInProgressModal } from "organisms/dispute-in-progress-modal/DisputeInProgressModal";
import { appLayoutLoader, type AppLayoutLoaderData } from "pages/root/AppLayout";
import { AnonymousLocationPrompt } from "pages/root/AnonymousLocationPrompt";
import { ShopLocationBanner } from "pages/root/shop/ShopLocationBanner";
import RootError from "pages/RootError";
import { clearAcknowledgedPopupPromotionIds } from "utils/functions";
import { isRetailProductsEnabled } from "utils/retailProducts.helpers";

/**
 * The same session rules as the app — sign-in, anonymous browsing, the handover after a visitor
 * verifies at checkout — for a practice that sells products at all. One that does not has no
 * storefront to show, so the visitor is sent to the app.
 */
export const standaloneProductsLayoutLoader: LoaderFunction = async (args) => {
  const data = (await appLayoutLoader(args)) as AppLayoutLoaderData | Response | null;
  if (data == null || data instanceof Response) return data;

  const platformSettings = data.platform?.platformSettings;
  if (!platformSettings || !isRetailProductsEnabled(platformSettings)) return redirect("/");

  return data;
};

/**
 * The frame around the products-only storefront: the practice's logo and the cart, and none of
 * the app's navigation, so a shopper sent here from the practice's own site stays among products.
 */
/** The storefront's own navigation, shown on every page so the cart and checkout always lead back. */
const StorefrontNavLink = ({ to, end, label }: { to: string; end?: boolean; label: string }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      cn(
        "whitespace-nowrap py-1 text-body2 transition-colors hover:text-primary",
        isActive ? "font-semibold text-primary" : "text-secondary",
      )
    }
  >
    {label}
  </NavLink>
);

export const StandaloneProductsLayout = () => {
  const { t } = useTranslation();
  const { user, platform, platformLocation, authClaims } = useLoaderData() as AppLayoutLoaderData;
  const navigate = useNavigate();
  const location = useLocation();
  const isAnonymousBrowsingActive = useIsAnonymousBrowsing();
  const locationBranding = useLocationLogo(platform?.id, platformLocation?.id, user.isGuest);
  const { platformDomainTokens } = usePlatformDomainTokens();

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;
  const showDisputeModal = !isAdminImpersonation && !!user.hasActiveDispute;
  const showSuspendedModal =
    !showDisputeModal && !isAdminImpersonation && !!user.hasPartialDisputePendingAdminAction;
  const platformName = platform?.name ?? "";
  const contactPhone = user.platformLocation?.phoneNumber ?? platformLocation?.phoneNumber;
  const contactEmail = user.platformLocation?.email ?? platformLocation?.email;
  const showSwitchLocations = (platformDomainTokens?.length ?? 0) > 1;

  // Sibling navigations skip the loader, so the account-only guard also holds at render time.
  if (isAnonymousBrowsingActive && !isAnonymousBrowsablePath(location.pathname)) {
    setPostLoginReturnTo(location.pathname + location.search);
    return <Navigate replace to="/signin" />;
  }

  return (
    <div className="contents" style={locationBrandingStyle(locationBranding?.brandColor)}>
      {platform && <WalletAvailabilityWarmup platform={platform} />}
      {/* A visitor has no home location, and the catalogue and its prices are per location. */}
      <AnonymousLocationPrompt />
      <DisputeInProgressModal
        open={showDisputeModal}
        platformName={platformName}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        showSwitchLocations={showSwitchLocations}
        onSwitchLocations={() => navigate("/platform-selector")}
      />
      <AccessTemporarilySuspendedModal
        open={showSuspendedModal}
        platformName={platformName}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        showSwitchLocations={showSwitchLocations}
        onSwitchLocations={() => navigate("/platform-selector")}
      />

      {/* Sticky and as tall as the app's desktop header, which the pages' sticky columns offset against. */}
      <header className="sticky top-0 z-30 border-b border-outline-grey bg-white pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex h-16 w-full max-w-screen-xl items-center justify-between px-5 desk:px-6 desklg:h-[72px] desklg:px-8 desk2xl:max-w-screen-2xl">
          <div className="flex min-w-0 items-center gap-5 desk:gap-8">
            <Link to={standaloneProductsPaths.home} className="flex h-8 shrink-0 items-center">
              {platform?.logoImageUrl ? (
                <img
                  src={platform.logoImageUrl}
                  alt={platformName}
                  className="h-8 max-w-28 object-contain desk:max-w-none"
                />
              ) : (
                <span className="text-title">{platformName}</span>
              )}
            </Link>
            <nav aria-label={t("shopPage.products")} className="flex items-center gap-4 desk:gap-6">
              <StorefrontNavLink
                to={standaloneProductsPaths.home}
                end
                label={t("shopProductsPage.allProducts")}
              />
              <StorefrontNavLink
                to={standaloneProductsPaths.browse}
                label={t("shopProductsListPage.browseTitle")}
              />
            </nav>
          </div>
          <div className="flex items-center">
            <TopNavActions />
          </div>
        </div>
      </header>
      {platform?.platformSettings?.patientShopLocationSwitchingEnabled && (
        <div className="mx-auto w-full max-w-screen-xl desk:px-2 desklg:px-4 desk2xl:max-w-screen-2xl">
          <ShopLocationBanner />
        </div>
      )}
      <div className="flex flex-auto">
        <div className="w-full">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

/** The cart and checkout pages are built for the app's centred column. */
export const StandaloneProductsCartColumn = () => (
  <div className="mx-auto w-full max-w-screen-xl desk2xl:max-w-screen-2xl">
    <Outlet />
  </div>
);

export const StandaloneProductsLayoutError = () => {
  const error = useRouteError();
  const location = useLocation();

  if (isAxiosError(error) && error.response?.status === 401) {
    clearAcknowledgedPopupPromotionIds();
    setPostLoginReturnTo(location.pathname + location.search);
    return <Navigate to="/signin" />;
  }

  return <RootError />;
};
